import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Check, Clock3, Mail, Phone, MapPin, CalendarClock, Inbox } from "lucide-react";
import AgendaCalendar from "@/components/admin/AgendaCalendar";

const TYPE_LABEL = { visio_1h: "Visio 1h", phone_15m: "Tél. 15 min", physical: "Physique" };
const STATUS = {
  demande: { l: "Demandé", c: "bg-[#FFDCA8] text-[#92400E]" },
  propose: { l: "Proposé", c: "bg-[#06B6D4] text-[#0A1128]" },
  confirme: { l: "Confirmé", c: "bg-green-600 text-white" },
  realise: { l: "Réalisé", c: "bg-slate-500 text-white" },
  annule: { l: "Annulé", c: "bg-red-500 text-white" },
};

export default function AdminAppointments() {
  const [items, setItems] = useState([]);
  const [propose, setPropose] = useState(null);
  const [proposalForm, setProposalForm] = useState({ proposed_date: "", proposed_time: "", note: "" });

  const load = () => api.get("/admin/appointments").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    const note = window.prompt("Message à inclure dans l'email de confirmation (optionnel) :", "");
    if (note === null) return;
    try { await api.post(`/admin/appointments/${id}/approve`, { note }); toast.success("Confirmé + email envoyé"); load(); }
    catch { toast.error("Erreur"); }
  };

  const sendPropose = async () => {
    if (!propose) return;
    if (!proposalForm.proposed_date || !proposalForm.proposed_time) return toast.error("Date et heure requises");
    try { await api.post(`/admin/appointments/${propose.id}/propose`, proposalForm); toast.success("Proposition envoyée par email"); setPropose(null); load(); }
    catch { toast.error("Erreur"); }
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/admin/appointments/${id}`, { status }); load(); } catch { toast.error("Erreur"); }
  };

  // Left panel = pending requests (demande + propose), sorted newest first
  const pending = useMemo(() => {
    return items
      .filter((a) => a.status === "demande" || a.status === "propose")
      .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  }, [items]);

  return (
    <div data-testid="admin-appointments">
      <div>
        <h1 className="display text-2xl sm:text-3xl text-[#0A1128] font-extrabold">Planning</h1>
        <p className="text-sm text-[#64748B] mt-1">Demandes à gauche · calendrier à droite. Approuvez ou proposez un autre créneau en 1 clic.</p>
      </div>

      <div className="mt-3 grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Left: pending requests (40%) */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-black/8 rounded-2xl overflow-hidden">
            <div className="px-4 py-3 border-b border-black/8 bg-[#FAFAFA] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Inbox size={16} className="text-[#06B6D4]" />
                <div className="display font-extrabold text-[#0A1128] text-sm">Demandes en attente</div>
              </div>
              <Badge className="bg-[#FFDCA8] text-[#92400E] text-[10px] font-bold" data-testid="appt-pending-count">{pending.length}</Badge>
            </div>

            <div className="max-h-[calc(100vh-240px)] overflow-y-auto" data-testid="appt-pending-list">
              {pending.length === 0 ? (
                <div className="p-10 text-center text-sm text-[#64748B]">
                  <Check size={28} className="mx-auto text-[#A7F3D0] mb-2" />
                  Aucune demande en attente. Tout est traité !
                </div>
              ) : (
                <ul className="divide-y divide-black/5">
                  {pending.map((a) => {
                    const st = STATUS[a.status];
                    return (
                      <li key={a.id} data-testid={`appt-${a.id}`} className="p-4 hover:bg-[#FAFAFA] transition-colors">
                        <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-[10px]">{TYPE_LABEL[a.type] || a.type}</Badge>
                            <Badge className={`${st.c} text-[10px] font-bold`}>{st.l}</Badge>
                          </div>
                          {a.payment_status === "paid" && <Badge className="bg-green-600 text-[10px]">Payé</Badge>}
                          {a.payment_status === "pending" && <Badge className="bg-[#FFDCA8] text-[#92400E] text-[10px]">€ pending</Badge>}
                        </div>

                        <div className="display font-extrabold text-[#0A1128]">{a.name}</div>
                        <div className="space-y-0.5 mt-1.5 text-xs text-[#475569]">
                          <div className="flex items-center gap-1.5"><Mail size={11} className="text-[#06B6D4]" /><span className="break-all">{a.email}</span></div>
                          {a.phone && <div className="flex items-center gap-1.5"><Phone size={11} className="text-[#06B6D4]" /><a href={`tel:${a.phone}`} className="hover:underline">{a.phone}</a></div>}
                          {a.location && <div className="flex items-center gap-1.5"><MapPin size={11} className="text-[#06B6D4]" />{a.location}</div>}
                        </div>

                        {(a.preferred_date || a.proposed_date) && (
                          <div className="mt-2 flex items-center gap-1.5 text-xs">
                            <CalendarClock size={12} className="text-[#06B6D4]" />
                            {a.proposed_date ? (
                              <span className="text-[#06B6D4] font-bold">Proposé : {a.proposed_date} {a.proposed_time}</span>
                            ) : (
                              <span className="text-[#475569]">Souhaité : <b>{a.preferred_date}</b> {a.preferred_time}</span>
                            )}
                          </div>
                        )}

                        {a.message && <div className="mt-2 text-xs text-[#64748B] bg-[#FAFAFA] border border-black/5 rounded p-2 italic">"{a.message}"</div>}

                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {a.status === "demande" && (
                            <>
                              <Button size="sm" data-testid={`appt-approve-${a.id}`} onClick={() => approve(a.id)} className="h-7 px-2.5 text-[11px] bg-green-600 hover:bg-green-700 text-white">
                                <Check size={11} className="mr-1" />Approuver
                              </Button>
                              <Button size="sm" variant="outline" data-testid={`appt-propose-${a.id}`} onClick={() => { setPropose(a); setProposalForm({ proposed_date: a.preferred_date || "", proposed_time: "", note: "" }); }} className="h-7 px-2.5 text-[11px]">
                                <Clock3 size={11} className="mr-1" />Autre créneau
                              </Button>
                            </>
                          )}
                          <select
                            value={a.status}
                            onChange={(e) => updateStatus(a.id, e.target.value)}
                            className="border border-black/10 rounded-md px-2 py-0.5 text-[11px] h-7"
                            data-testid={`appt-status-${a.id}`}
                          >
                            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
                          </select>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Right: calendar (60%) */}
        <div className="lg:col-span-3" data-testid="appt-calendar">
          <AgendaCalendar appointments={items} onRefresh={load} />
        </div>
      </div>

      <Dialog open={!!propose} onOpenChange={(v) => !v && setPropose(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Proposer un autre créneau</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Date</Label><Input type="date" data-testid="propose-date" value={proposalForm.proposed_date} onChange={(e) => setProposalForm({ ...proposalForm, proposed_date: e.target.value })} /></div>
            <div><Label>Heure</Label><Input data-testid="propose-time" placeholder="14:30" value={proposalForm.proposed_time} onChange={(e) => setProposalForm({ ...proposalForm, proposed_time: e.target.value })} /></div>
            <div><Label>Message (optionnel)</Label><Textarea rows={3} data-testid="propose-note" value={proposalForm.note} onChange={(e) => setProposalForm({ ...proposalForm, note: e.target.value })} placeholder="Expliquez brièvement pourquoi vous proposez un autre créneau..." /></div>
          </div>
          <DialogFooter><Button onClick={sendPropose} data-testid="propose-send" className="bg-[#0A1128] text-white">Envoyer la proposition</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
